/**
 * @file sheets/shared/scrim.tsx
 *
 * Animated backdrop overlay (Scrim) for Modal Sheet variants.
 *
 * Supports animated opacity via `style.opacity` (MotionValue) so the scrim
 * fades in real-time as the user drags the sheet toward dismiss position.
 *
 * @see https://m3.material.io/components/sheets-bottom/guidelines#b0025900-5e5b-4c4a-9a76-e0d09d63afdb
 */

import type { MotionStyle } from "motion/react";
import { m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import type { ScrimProps } from "./types";

const SCRIM_ANIM = {
	initial: { opacity: 0 },
	animate: {
		opacity: 1,
		transition: { duration: 0.2, ease: "easeOut" as const },
	},
	exit: { opacity: 0, transition: { duration: 0.18, ease: "easeIn" as const } },
} as const;

/**
 * Translucent backdrop rendered behind modal sheet variants.
 * Clicking the scrim triggers `onClick`.
 * Pass `style.opacity` as a MotionValue to link scrim opacity with sheet drag position.
 */
export const Scrim = React.forwardRef<HTMLDivElement, ScrimProps>(
	({ onClick, className, style }, ref) => (
		<m.div
			ref={ref}
			aria-hidden="true"
			className={cn("fixed inset-0 z-40 bg-black/40", className)}
			onClick={onClick}
			style={style as MotionStyle}
			{...SCRIM_ANIM}
		/>
	),
);

Scrim.displayName = "Scrim";
